// frontend/src/features/ogdb2/utils/capitolDcf.js

/**
 * Capitol-style lease DCF (approx.)
 * ---------------------------------
 * Goal: compute apples-to-apples Working Interest value the way the lease summary implies:
 * - forecast volumes from start rate + exponential decline (N=1)
 * - compute gross revenue from prices
 * - compute royalty PV as royalty% * PV(gross revenue)
 * - compute working CF as (1-royalty%) * revenue - LOE_t
 * - LOE_t is a fixed/variable blend that scales partially with revenue:
 *     LOE_t = LOE1 * (fixed_share + variable_share * (Rev_t / Rev_1))
 * - discount mid-year: t - 0.5
 * - apply equipment floor:
 *     WI_value = max(NetEquipmentValue, PV(WI cashflows) + NetEquipmentValue)
 */

function toNum(x) {
  if (x === null || x === undefined) return null;
  const s = String(x).trim();
  if (!s) return null;
  const n = Number(s.replace(/,/g, ""));
  return Number.isFinite(n) ? n : null;
}

function pctToFrac(pct) {
  const n = toNum(pct);
  if (n === null) return null;
  return n / 100.0;
}

function clamp(v, lo, hi) {
  return Math.max(lo, Math.min(hi, v));
}

function annualVolumeExponential(q0PerDay, declinePerYearFrac, yearIndex0) {
  // q at start of year t: q0 * exp(-D * t)
  const qStart = q0PerDay * Math.exp(-declinePerYearFrac * yearIndex0);

  if (!declinePerYearFrac || declinePerYearFrac <= 0) {
    return qStart * 365.0;
  }

  // integrate exp decline over 1 year
  // integral qStart*exp(-D*s) ds from 0..1 = qStart*(1-exp(-D))/D
  const yearAvgIntegral = (1 - Math.exp(-declinePerYearFrac)) / declinePerYearFrac;
  return qStart * yearAvgIntegral * 365.0;
}

export function computeCapitolDCF({
  scenarioKey,
  getMetricValue,
  // Optional: BA override for Comparable LOE (stored as metric_code loe_comparable scenario ba)
  getComparableLOE,
}) {
  // -------- Inputs (fallbacks are intentional) --------
  const royaltyFrac = pctToFrac(getMetricValue({ code: "royalty_interest_pct", type: "percent" }, scenarioKey))
    ?? pctToFrac(getMetricValue({ code: "royalty_interest_pct", type: "percent" }, "assessor"))
    ?? 0.25;

  // Economic life (years)
  const lifeYears =
    toNum(getMetricValue({ code: "economic_life_years", type: "number" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "economic_life_years", type: "number" }, "assessor")) ??
    3;

  const years = clamp(Math.round(lifeYears), 1, 50);

  // Discount rate
  const rPct =
    toNum(getMetricValue({ code: "discount_rate", type: "percent" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "discount_rate", type: "percent" }, "assessor")) ??
    19.0;

  const r = (rPct / 100.0);

  // Start rates + declines
  const oilStart =
    toNum(getMetricValue({ code: "comparable_oil_start_rate", type: "number" }, scenarioKey)) ??
	toNum(getMetricValue({ code: "comparable_oil_start_rate", type: "number" }, "assessor")) ??
    0;

  const gasStart =    
	toNum(getMetricValue({ code: "comparable_gas_start_rate", type: "number" }, scenarioKey)) ??
	toNum(getMetricValue({ code: "comparable_gas_start_rate", type: "number" }, "assessor")) ??
    0;

  const oilDeclinePct =
    toNum(getMetricValue({ code: "oil_decline_rate", type: "percent" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "oil_decline_rate", type: "percent" }, "assessor")) ??
    toNum(getMetricValue({ code: "decline_rate", type: "percent" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "decline_rate", type: "percent" }, "assessor")) ??
    0;

  const gasDeclinePct =
    toNum(getMetricValue({ code: "gas_decline_rate", type: "percent" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "gas_decline_rate", type: "percent" }, "assessor")) ??
    toNum(getMetricValue({ code: "decline_rate", type: "percent" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "decline_rate", type: "percent" }, "assessor")) ??
    0;

  const oilD = clamp((oilDeclinePct / 100.0), 0, 5);
  const gasD = clamp((gasDeclinePct / 100.0), 0, 5);

  // Prices
  const oilPrice =
    toNum(getMetricValue({ code: "oil_price", type: "number" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "oil_price", type: "number" }, "assessor")) ??
    0;

  const gasPrice =
    toNum(getMetricValue({ code: "gas_price", type: "number" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "gas_price", type: "number" }, "assessor")) ??
    0;

  // LOE1: use comparable LOE logic
  const loe1 = toNum(getComparableLOE?.(scenarioKey)) ?? 0;

  // LOE variable share % (Capitol-ish)
  const varSharePct =
    toNum(getMetricValue({ code: "loe_variable_share_pct", type: "percent" }, scenarioKey)) ??
    (scenarioKey === "ba"
      ? toNum(getMetricValue({ code: "loe_variable_share_pct", type: "percent" }, "ba"))
      : null) ??
    12.0; // default 12%

  const variableShare = clamp((varSharePct / 100.0), 0, 1);
  const fixedShare = 1 - variableShare;

  // Equipment value (floor / salvage-ish)
  const netEquip =
    toNum(getMetricValue({ code: "net_equipment_value", type: "money" }, scenarioKey)) ??
    toNum(getMetricValue({ code: "net_equipment_value", type: "money" }, "assessor")) ??
    0;

  // -------- Forecast + PV --------
  const rows = [];

  // First year revenue for scaling LOE
  const oilY1 = annualVolumeExponential(oilStart, oilD, 0);
  const gasY1 = annualVolumeExponential(gasStart, gasD, 0);
  const rev1 = (oilY1 * oilPrice) + (gasY1 * gasPrice);

  let pvGrossRev = 0;
  let pvRoyalty = 0;
  let pvWorkingCF = 0;

  for (let t = 1; t <= years; t++) {
    const yearIndex0 = t - 1;

    const oilVol = annualVolumeExponential(oilStart, oilD, yearIndex0);
    const gasVol = annualVolumeExponential(gasStart, gasD, yearIndex0);

    const grossRev = (oilVol * oilPrice) + (gasVol * gasPrice);

    const loeT = rev1 > 0 ? loe1 * (fixedShare + variableShare * (grossRev / rev1)) : loe1;

    const royaltyCF = royaltyFrac * grossRev;
    const workingCF = (1 - royaltyFrac) * grossRev - loeT;

    // mid-year discounting
    const exp = (t - 0.5);
    const df = 1 / Math.pow(1 + r, exp);

    pvGrossRev += grossRev * df;
    pvRoyalty += royaltyCF * df;
    pvWorkingCF += workingCF * df;

    rows.push({
      year: t,
      oilVol,
      gasVol,
      grossRev,
      loe: loeT,
      royaltyCF,
      workingCF,
      df,
      pvRoyalty: royaltyCF * df,
      pvWorking: workingCF * df,
    });
  }

  const computedRoyaltyValue = pvRoyalty;
  const computedWorkingValueRaw = pvWorkingCF + netEquip;
  const computedWorkingValue = Math.max(netEquip, computedWorkingValueRaw);
  const floorApplied = computedWorkingValue === netEquip && computedWorkingValueRaw < netEquip;

  return {
    inputs: {
      royaltyFrac,
      years,
      rPct,
      oilStart,
      gasStart,
      oilDeclinePct,
      gasDeclinePct,
      oilPrice,
      gasPrice,
      loe1,
      varSharePct,
      netEquip,
    },
    pvGrossRev,
    computedRoyaltyValue,
    pvWorkingCF,
    computedWorkingValueRaw,
    computedWorkingValue,
    floorApplied,
    totalLeaseValue: computedRoyaltyValue + computedWorkingValue,
    rows,
  };
}
